/**
 * Client-side mirror of `shared.validators.validate_stockmock_url`.
 *
 * The backend stays the source of truth on the server-side gate; this
 * frontend copy is so the create / edit basket dialogs can give inline
 * feedback before submit instead of round-tripping for the error.
 *
 * Keep the rule set in sync with the Python validator. If you tweak one,
 * tweak both.
 */

/** Hostnames allowed for the optional StockMock URL on baskets. */
export const STOCKMOCK_URL_ALLOWED_HOSTS = ['stockmock.in', 'www.stockmock.in'];
export const STOCKMOCK_URL_MAX_LENGTH = 500;

export interface StockmockUrlCheck {
  /** Normalized URL when valid, empty string when input is blank, null on failure. */
  value: string | null;
  /** Human-readable error, null on success. */
  error: string | null;
}

/**
 * Validate the optional StockMock URL.
 *
 * Returns `{ value: '', error: null }` for blank input (the field is optional).
 * Returns the trimmed URL with no error when valid.
 * Returns the original input plus an error message when invalid.
 */
export function validateStockmockUrl(raw: string | null | undefined): StockmockUrlCheck {
  if (raw == null) return { value: '', error: null };
  const stripped = raw.trim();
  if (!stripped) return { value: '', error: null };

  if (stripped.length > STOCKMOCK_URL_MAX_LENGTH) {
    return { value: null, error: `URL must be at most ${STOCKMOCK_URL_MAX_LENGTH} characters` };
  }

  let parsed: URL;
  try {
    parsed = new URL(stripped);
  } catch {
    return { value: null, error: 'Not a valid URL' };
  }

  if (parsed.protocol !== 'https:') {
    return { value: null, error: 'URL must use https://' };
  }
  if (parsed.username || parsed.password) {
    return { value: null, error: 'URL must not contain credentials' };
  }
  const host = parsed.hostname.toLowerCase();
  if (!STOCKMOCK_URL_ALLOWED_HOSTS.includes(host)) {
    return { value: null, error: `URL host must be one of: ${STOCKMOCK_URL_ALLOWED_HOSTS.join(', ')}` };
  }

  return { value: stripped, error: null };
}
